import { useState } from "react";

import { parseNumber } from "common/utils/numbers";
import { PatientStatus, WeightUnitOfMeasure } from "common/types";

import Select from "client/common/components/inputs/Select";
import Banner from "client/common/components/content/Banner";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useCurrentDrawer } from "client/common/hooks/stackables";

import { useClientQuery, useUpdatePatientMutation } from "../../hooks";
import PetForm from "../forms/PetForm";

const noop = () => undefined;

const SurrenderedStatus = "SURRENDERED";

export type AccountUpdatePetDrawerProps = {
  id: number;
  onSuccess?: () => void;
};

const AccountUpdatePetDrawer = ({ id, onSuccess = noop }: AccountUpdatePetDrawerProps) => {
  const { data: client, isLoading: isClientLoading, error: clientError } = useClientQuery();

  const pet = client ? client.patients.find((patient) => patient.id === id) : undefined;
  const [status, setStatus] = useState<PatientStatus | string>(PatientStatus.Active);
  const { close } = useCurrentDrawer();
  const handleOnSuccess = () => {
    close();
    onSuccess();
  };

  const {
    mutate: updatePatient,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdatePatientMutation(id, { onSuccess: handleOnSuccess });

  if (isClientLoading) {
    return <DrawerLayout.Loading />;
  }

  if (!pet || clientError) {
    return <DrawerLayout.FetchError entityName="pet" error={clientError} />;
  }

  return (
    <DrawerLayout>
      <DrawerLayout.Header title="Update pet" onClose={close} />
      <PetForm
        submitText="Update Pet"
        isLoading={isUpdating}
        initialValues={{
          name: pet.name ?? "",
          sex: pet.sex,
          weight_in_units: pet.weight_in_units?.toString() ?? "",
          weight_unit_of_measure: pet.weight_unit_of_measure || WeightUnitOfMeasure.Pound,
          birth_day: pet.birth_day?.toString() ?? "",
          birth_month: pet.birth_month?.toString() ?? "",
          birth_year: pet.birth_year?.toString() ?? "",
          species: pet.species,
          custom_species: pet.custom_species ?? "",
          allergies: pet.allergies,
          image: pet.image,
        }}
        onSubmit={(values) =>
          updatePatient({
            ...values,
            weight_in_units: parseNumber(values.weight_in_units, 0) || null,
            weight_unit_of_measure: values.weight_unit_of_measure,
            birth_day: parseNumber(values.birth_day, 0) || null,
            birth_month: parseNumber(values.birth_month, 0) || null,
            birth_year: parseNumber(values.birth_year, 0) || null,
            should_deactivate_client_relationship: status === SurrenderedStatus,
            status: status === SurrenderedStatus ? undefined : (status as PatientStatus),
            image_id: values.image?.id ?? null,
          })
        }
        error={updateError}
        renderExtraContent={() => (
          <DrawerLayout.Form.Group>
            <Select
              label="Status"
              value={status}
              errors={updateError?.errors.status}
              onChange={(val) => setStatus(val as PatientStatus)}
            >
              <Select.Option value={PatientStatus.Active}>Active</Select.Option>
              <Select.Option value={SurrenderedStatus}>Surrendered</Select.Option>
              <Select.Option value={PatientStatus.Deceased}>Deceased</Select.Option>
            </Select>
            {status !== PatientStatus.Active && (
              <Banner variant="warning">
                <Banner.Text
                  align="left"
                  label={`We're sorry to hear about that. We'll automatically deactivate any prescriptions and subscriptions for ${pet.name_display}. Please note that this action cannot be undone, but you can always reach out if you need any assistance.`}
                />
              </Banner>
            )}
          </DrawerLayout.Form.Group>
        )}
      />
    </DrawerLayout>
  );
};

export default AccountUpdatePetDrawer;
